import { useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { tr } from 'react-hook-form-auto'
import {
  DndContext,
  PointerSensor,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'

import { Icon } from '../../ui/bootstrap/bootstrap'

import styles from './MealTimeline.sass'
import {
  PX_PER_SECOND,
  axisTicks,
  assignLanes,
  barWidth,
  colorIndex,
  endOf,
  formatTime,
  laneIds,
  numVal,
  segmentHeight,
  stripHeight
} from './timelineShared'

const newId = () => {
  if (globalThis.crypto && globalThis.crypto.randomUUID)
    return globalThis.crypto.randomUUID()

  return 'seg-' + Date.now() + '-' + Math.random().toString(36).slice(2)
}

const newSegment = () => ({
  id: newId(),
  kind: 'interval',
  start: 0,
  duration: 600,
  label: '',
  ingredientIds: []
})

const Axis = ({ maxEnd }) => {
  const { majorTicks, minorTicks } = axisTicks(maxEnd)

  return (
    <div className={styles.axis}>
      {minorTicks.map(t =>
        <div
          key={t}
          className={styles.tickMinor}
          style={{ left: t * PX_PER_SECOND }}
        />)}
      {majorTicks.map(t =>
        <div
          key={t}
          className={styles.tick}
          style={{ left: t * PX_PER_SECOND }}
        >
          <span className={styles.tickLabel}>{formatTime(t)}</span>
        </div>)}
    </div>
  )
}

const IngredientChip = ({ ingredient }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({ id: `ingredient:${ingredient.id}` })

  return (
    <span
      ref={setNodeRef}
      className={styles.chip}
      style={{
        transform: CSS.Transform.toString(transform),
        opacity: isDragging ? 0.4 : 1
      }}
      {...attributes}
      {...listeners}
    >
      <Icon icon="tag" />
      <span className={styles.chipName}>{ingredient.name}</span>
    </span>
  )
}

const EditorFields = ({ segment, onUpdate, onRemove }) => {
  const isInterval = segment.kind == 'interval'

  const setLabel = e => onUpdate(segment.id, { label: e.target.value })
  const setStart = e =>
    onUpdate(segment.id, { start: Math.max(0, numVal(e.target.value)) })
  const setDuration = e =>
    onUpdate(segment.id, { duration: Math.max(0, numVal(e.target.value)) })
  const toggleKind = () =>
    onUpdate(segment.id, { kind: isInterval ? 'punctual' : 'interval' })

  return (
    <div className={styles.segBody}>
      <div className={styles.segTop}>
        <button
          type="button"
          className={styles.kindToggle}
          onClick={toggleKind}
          title={isInterval
            ? tr('models.meal.timeline.kind.interval')
            : tr('models.meal.timeline.kind.punctual')}
        >
          <Icon icon={isInterval ? 'hourglass-start' : 'check-circle'} />
        </button>
        <input
          type="text"
          className={styles.labelInput}
          value={segment.label || ''}
          onChange={setLabel}
          placeholder={tr('models.meal.timeline.label')}
        />
        <button
          type="button"
          className={styles.remove}
          onClick={() => onRemove(segment.id)}
        >
          <Icon icon="trash" />
        </button>
      </div>
      <div className={styles.segTimes}>
        <span className={styles.timeField}>
          <input
            type="number"
            min="0"
            value={segment.start || 0}
            onChange={setStart}
            aria-label={tr('models.meal.timeline.start')}
          />
          <span className={styles.timeUnit}>s</span>
        </span>
        {isInterval &&
          <span className={styles.timeField}>
            <input
              type="number"
              min="0"
              value={segment.duration || 0}
              onChange={setDuration}
              aria-label={tr('models.meal.timeline.duration')}
            />
            <span className={styles.timeUnit}>s</span>
          </span>}
      </div>
    </div>
  )
}

const summary = (segment) => {
  const start = formatTime(segment.start || 0)
  const line = segment.kind == 'interval'
    ? `${start} · ${Math.round(segment.duration || 0)}s`
    : start

  return [ segment.label, line ].filter(Boolean).join(' · ')
}

const Bar = ({ segment, ingredients, onOpenEditor, onRemoveIngredient }) => {
  const { attributes, listeners, setNodeRef } =
    useDraggable({ id: `segment:${segment.id}` })
  const colorClass = styles['color' + colorIndex(segment.id)]
  const ingNames = (segment.ingredientIds || [])
    .map(id => ingredients.find(ing => ing.id == String(id)))
    .filter(Boolean)

  const handleClick = e => {
    e.stopPropagation()
    onOpenEditor(segment, e.currentTarget)
  }

  const handleRemoveIngredient = (e, ingredientId) => {
    e.stopPropagation()
    onRemoveIngredient(segment.id, ingredientId)
  }

  return (
    <div
      ref={setNodeRef}
      className={`${styles.segBar} ${colorClass}`}
      style={{ height: segmentHeight(segment) }}
      title={summary(segment)}
      onClick={handleClick}
      {...attributes}
      {...listeners}
    >
      <span className={styles.grip} />
      <div className={styles.barContent}>
        {segment.label &&
          <span className={styles.barLabel}>{segment.label}</span>}
        {ingNames.length > 0 &&
          <div className={styles.barIngredients}>
            {ingNames.map(ing =>
              <span key={ing.id} className={styles.segIng}>
                {ing.name}
                <button
                  type="button"
                  className={styles.segIngRemove}
                  onClick={e => handleRemoveIngredient(e, String(ing.id))}
                >
                  <Icon icon="times" />
                </button>
              </span>)}
          </div>}
      </div>
    </div>
  )
}

const ResizeHandle = ({ segment }) => {
  const { attributes, listeners, setNodeRef } =
    useDraggable({ id: `resize:${segment.id}` })

  return (
    <div
      ref={setNodeRef}
      className={styles.resize}
      {...attributes}
      {...listeners}
    />
  )
}

const Segment = ({
  segment,
  ingredients,
  onRemove,
  onRemoveIngredient,
  onOpenEditor
}) => {
  const isInterval = segment.kind == 'interval'
  const { setNodeRef } = useDroppable({ id: `segment:${segment.id}` })

  return (
    <div
      className={styles.segWrap}
      style={{
        left: (segment.start || 0) * PX_PER_SECOND,
        width: barWidth(segment)
      }}
    >
      <div ref={setNodeRef} className={styles.drop}>
        <Bar
          segment={segment}
          ingredients={ingredients}
          onOpenEditor={onOpenEditor}
          onRemoveIngredient={onRemoveIngredient}
        />
      </div>
      {isInterval && <ResizeHandle segment={segment} />}
    </div>
  )
}

const EditPopover = ({
  segment,
  onUpdate,
  onRemove,
  rect,
  onClose
}) => {
  const left = Math.max(8, Math.min(rect.left, window.innerWidth - 320))
  const top = Math.min(rect.bottom + 8, window.innerHeight - 220)

  const handleRemove = () => {
    onRemove(segment.id)
    onClose()
  }

  return (
    <>
      <div className={styles.popoverBackdrop} onClick={onClose} />
      <div className={styles.popover} style={{ top, left }}>
        <div className={styles.popoverHeader}>
          <span className={styles.popoverTitle}>
            {tr('models.meal.timeline.segment')}
          </span>
          <button
            type="button"
            className={styles.popoverClose}
            onClick={onClose}
          >
            <Icon icon="times" />
          </button>
        </div>
        <EditorFields
          segment={segment}
          onUpdate={onUpdate}
          onRemove={handleRemove}
        />
      </div>
    </>
  )
}

export const MealTimeline = ({ timeline = [], ingredients = [], onChange }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
  )

  const dragRef = useRef(null)
  const suppressClick = useRef(false)
  const [ frozenLanes, setFrozenLanes ] = useState(null)
  const [ editor, setEditor ] = useState(null)

  const intervals = timeline.filter(s => s.kind == 'interval')
  const punctuals = timeline.filter(s => s.kind == 'punctual')

  const lanes = frozenLanes
    ? frozenLanes
      .map(ids => ids.map(id => timeline.find(s => s.id == id)).filter(Boolean))
    : assignLanes(intervals)

  const maxEnd = timeline.reduce((max, s) => Math.max(max, endOf(s)), 0)
  const canvasWidth = Math.max(420, maxEnd * PX_PER_SECOND + 240)

  const update = (id, patch) =>
    onChange(timeline.map(s => s.id == id ? { ...s, ...patch } : s))
  const remove = id => onChange(timeline.filter(s => s.id != id))
  const add = () => onChange([ ...timeline, newSegment() ])

  const addIngredient = (segmentId, ingredientId) => {
    const segment = timeline.find(s => s.id == segmentId)
    if (!segment)
      return

    const list = segment.ingredientIds || []
    if (list.indexOf(ingredientId) != -1)
      return

    update(segmentId, { ingredientIds: [ ...list, ingredientId ] })
  }

  const removeIngredient = (segmentId, ingredientId) => {
    const segment = timeline.find(s => s.id == segmentId)
    if (!segment)
      return

    update(segmentId, {
      ingredientIds: (segment.ingredientIds || [])
        .filter(id => id != ingredientId)
    })
  }

  const openEditor = (segment, node) => {
    if (suppressClick.current) {
      suppressClick.current = false
      return
    }

    setEditor({ id: segment.id, rect: node.getBoundingClientRect() })
  }

  const closeEditor = () => setEditor(null)

  const handleDragStart = ({ active }) => {
    const activeId = String(active.id)

    if (activeId.indexOf('segment:') == 0) {
      const id = activeId.slice('segment:'.length)
      const segment = timeline.find(s => s.id == id)

      if (segment) {
        dragRef.current = { id, kind: 'move', origin: { start: segment.start || 0 } }
        setFrozenLanes(laneIds(intervals))
      }
    } else if (activeId.indexOf('resize:') == 0) {
      const id = activeId.slice('resize:'.length)
      const segment = timeline.find(s => s.id == id)

      if (segment) {
        dragRef.current = { id, kind: 'resize', origin: { duration: segment.duration || 0 } }
        setFrozenLanes(laneIds(intervals))
      }
    }
  }

  const handleDragMove = ({ delta }) => {
    const drag = dragRef.current
    if (!drag)
      return

    const deltaSeconds = Math.round(delta.x / PX_PER_SECOND)

    if (drag.kind == 'move')
      update(drag.id, { start: Math.max(0, drag.origin.start + deltaSeconds) })
    else
      update(drag.id, { duration: Math.max(0, drag.origin.duration + deltaSeconds) })
  }

  const handleDragEnd = ({ active, over }) => {
    const activeId = String(active.id)

    if (activeId.indexOf('ingredient:') == 0 && over) {
      const overId = String(over.id)
      if (overId.indexOf('segment:') == 0)
        addIngredient(
          overId.slice('segment:'.length),
          activeId.slice('ingredient:'.length)
        )
    }

    dragRef.current = null
    setFrozenLanes(null)

    if (activeId.indexOf('ingredient:') != 0) {
      suppressClick.current = true
      setTimeout(() => { suppressClick.current = false }, 0)
    }
  }

  const handleDragCancel = () => {
    dragRef.current = null
    setFrozenLanes(null)
  }

  const editingSegment = editor && timeline.find(s => s.id == editor.id)

  return (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        <button type="button" className={styles.addButton} onClick={add}>
          <Icon icon="plus" text={tr('models.meal.timeline.addSegment')} />
        </button>
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={handleDragStart}
        onDragMove={handleDragMove}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <div className={styles.palette}>
          <div className={styles.paletteHint}>
            {tr('models.meal.timeline.dragHint')}
          </div>
          <div className={styles.paletteChips}>
            {ingredients.map(ingredient =>
              <IngredientChip key={ingredient.id} ingredient={ingredient} />)}
          </div>
        </div>
        <div className={styles.canvas} style={{ width: canvasWidth }}>
          <Axis maxEnd={maxEnd} />
          {punctuals.length > 0 &&
            <div
              className={styles.markerStrip}
              style={{ height: stripHeight(punctuals) }}
            >
              {punctuals.map(segment =>
                <Segment
                  key={segment.id}
                  segment={segment}
                  ingredients={ingredients}
                  onRemove={remove}
                  onRemoveIngredient={removeIngredient}
                  onOpenEditor={openEditor}
                />)}
            </div>}
          {lanes.map((lane, idx) =>
            <div
              key={idx}
              className={styles.lane}
              style={{ height: stripHeight(lane) }}
            >
              {lane.map(segment =>
                <Segment
                  key={segment.id}
                  segment={segment}
                  ingredients={ingredients}
                  onRemove={remove}
                  onRemoveIngredient={removeIngredient}
                  onOpenEditor={openEditor}
                />)}
            </div>)}
        </div>
      </DndContext>
      {editingSegment && typeof document != 'undefined' && createPortal(
        <EditPopover
          segment={editingSegment}
          onUpdate={update}
          onRemove={remove}
          rect={editor.rect}
          onClose={closeEditor}
        />,
        document.body
      )}
    </div>
  )
}
